import * as React from 'react';
import DropdownItem from 'Douleutaras/components/Dropdown/DropdownItem';
import { URLS } from 'Douleutaras/constants/urls';
import { selectAuthState, selectCurrentUser, setUserType, userLogout } from 'Douleutaras/redux/data/auth/authSlice';
import { User, UserType } from 'Douleutaras/types';
import { useIsAnonymizedRoute } from 'Douleutaras/hooks/useIsAnonymizedRoute';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import LanguageSwitcher from 'Douleutaras/layout/partials/LanguageSwitcher';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { useTranslation } from 'react-i18next';
import { getAbsoluteUrl } from 'Douleutaras/utilities/url-builder';
import { useMatch, useNavigate } from 'react-router-dom';
import { useLocalization } from 'Douleutaras/hooks/localization';
import { useAB } from 'Douleutaras/app/lib/ab';

import './DefaultHeaderUserMenu.scss';

interface DefaultHeaderUserMenuProps {
  isPro: boolean;
  isSettingsUrl: boolean;
  setUserDropdown(open: boolean): void;
}

export const DefaultHeaderUserMenu = ({
    isPro,
    isSettingsUrl,
    setUserDropdown
  }: DefaultHeaderUserMenuProps) => {
  const { t } = useTranslation();
  const { countryCode } = useLocalization();
  const navigate = useNavigate();
  const { abService } = useAB();

  const isAnonymizedRoute = useIsAnonymizedRoute();
  const [ mounted, setMounted ] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  const isResolvingAuth = isAnonymizedRoute && !mounted;
  const rawUser = useAppSelector(selectCurrentUser);
  const user = isResolvingAuth ? ({} as User) : rawUser;
  const { loading: userLoading } = useAppSelector(selectAuthState);
  const dispatch = useAppDispatch();
  const ff = useAppSelector(state => state.featureFlags.entities);

  const isLoggedIn = user && Boolean(Object.keys(user).length);
  const settingsUrl = isPro ? getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.GENERAL') : getAbsoluteUrl('CLIENT_DASHBOARD.SETTINGS');
  const isInvoicesActive = Boolean(useMatch({
    path: getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.TRANSACTIONS.INVOICES'),
    end: false,
    caseSensitive: true
  }));

  return (
    <>
      { isLoggedIn &&
        <div styleName="user-dropdown-intro">
          <div styleName="user-dropdown-intro__title">
            {t('Hello')}, {user.first_name_vocative}
          </div>
        </div>
      }
      <footer styleName="user-dropdown-footer">
        {!isLoggedIn && <>
          <div styleName="dropdown-item">
            <DropdownItem
              icon={<span className="dashicon dashicon-user-solid"/>}
              text={t('Log in')}
              href={URLS.AUTHENTICATION.LOGIN}
              onClick={() => setUserDropdown(false)}
            />
          </div>
          <div styleName="dropdown-item">
            <DropdownItem
              icon={<span className="dashicon dashicon-user-pro-solid"/>}
              text={t('Professional Registration')}
              href={URLS.AUTHENTICATION.REGISTER_PRO}
              onClick={() => setUserDropdown(false)}
            />
          </div>
        </>}
        {isLoggedIn &&
          <>
            {isPro &&
              <>
              <div styleName="dropdown-item">
                <DropdownItem
                  icon={<span className="dashicon dashicon-invoices"/>}
                  text={t('Invoices')}
                  isReactRoute
                  href={getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.TRANSACTIONS.INVOICES')}
                  dataTracking="pro-dashboard-navbar__profile-invoices"
                  active={isInvoicesActive || false}
                  onClick={() => setUserDropdown(false)}
                />
              </div>
                { ff.proMonthlyBonusEnabled &&
                  <DropdownItem
                    icon={<div className="dashicon dashicon-cashback" />}
                    text={t('T.HEADER.Cashback')}
                    isReactRoute
                    href={getAbsoluteUrl('URLS.PRO_DASHBOARD.BONUS')}
                    onClick={() => setUserDropdown(false)}
                  />
                }
              </>
            }
            <div styleName="dropdown-item">
              <DropdownItem
                icon={<span className="dashicon dashicon-settings"/>}
                text={t('Settings')}
                isReactRoute
                href={settingsUrl}
                dataTracking={isPro ? 'pro-dashboard-navbar__profile-settings' : ''}
                onClick={() => setUserDropdown(false)}
                active={isSettingsUrl}
              />
            </div>
            { isPro
              ? <>
                  <div styleName="dropdown-item">
                    <DropdownItem
                      icon={<span className="dashicon dashicon-headset"/>}
                      text={t('T.HEADER.SUPPORT')}
                      onClick={() => {
                        navigate(URLS.CONTACT_US.INDEX);
                      }}
                      dataTracking="pro-dashboard-navbar__profile-pro-support"
                      isButton
                    />
                  </div>
                  <div styleName="dropdown-item">
                    <DropdownItem
                      icon={<span className="dashicon dashicon-toggle"/>}
                      disabled={userLoading}
                      text={t('T.CUSTOMER.VIEW')}
                      onClick={async () => {
                        await dispatch(setUserType({id: user.id, type: UserType.BUYER}));
                        navigate(URLS.CLIENT_DASHBOARD.INDEX);
                      }}
                      dataTracking="pro-dashboard-navbar__profile-switch-account"
                      isButton
                    />
                  </div>
                </>
              : user?.seller &&
                 <div styleName="dropdown-item">
                   <DropdownItem
                     disabled={userLoading}
                     icon={<span className="dashicon dashicon-toggle"/>}
                     text={t('Switch to professional account')}
                     onClick={async () => {
                       await dispatch(setUserType({id: user.id, type: UserType.SELLER}));
                       navigate(URLS.SELLER.MY_JOBS);
                     }}
                     isButton
                   />
                 </div>
            }
            <div styleName="dropdown-item">
              <DropdownItem
                icon={<span className="dashicon dashicon-sign-out"/>}
                text={t('Logout')}
                onClick={() => {
                  dispatch(userLogout({id: user.id}));

                  // @AB-Service
                  abService.reset();
                }}
                dataTracking={isPro ? 'pro-dashboard-navbar__sign-out' : ''}
              />
            </div>
          </>
        }
        {COUNTRY_CONFIG.LANGUAGES_SUPPORTED[countryCode].length > 1 &&
          <>
            <div styleName="user-dropdown-splitter"/>
            <LanguageSwitcher isDropdownSwitcher={false}/>
          </>
        }
      </footer>
    </>
  );
};

export default DefaultHeaderUserMenu;
